import React, { useState } from "react";
import useForm from "../../../../hooks/useForm";
import Form from "../../../form/Form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "../../../form/Button";
import Input from "../../../form/Input";
import {
  invitation,
  invitationEdit,
} from "../../../../services/organizationService";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useOrganization } from "../../../../context/OrganizationContext";
import { useDebounce } from "../../../../hooks/useDebounce";
import {
  referVerificationDetails,
  ticketList,
} from "../../../../services/ticketService";
import { toast } from "react-toastify";
import Select from "../../../form/select/Select";
import SelectAlt from "../../../form/select/Select2";
import { lngDetect } from "../../../../helpers/lngDetect";
import Table from "../../../elements/table/Table";

const validation = {
  name: { required: true, message: "Please enter your name!" },
  email: { required: true, message: "Please enter your email!" },
  customConditionDiscount: {
    type: "number",
    message: "invalid input type. Please enter number (0-9)",
  },
  customConditionCommission: {
    type: "number",
    message: "invalid input type. Please enter number (0-9)",
  },
};

const ManagePromoter = ({ getResponse = () => {}, data = {} }) => {
  const queryClient = useQueryClient();
  const { organizations, selectedOrganization } = useOrganization();
  const { lang } = useSelector((state) => state?.langProvider);
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [filters, setFilters] = useState({
    limit: 10000,
    page: 1,
    sortQuery: "-createdAt",
    organization: selectedOrganization,
  });
  const { event, eventLoading, selectedLanguage } = useOutletContext();
  const [settingsPerTicket, setSettingsPerTicket] = useState({
    ticket: { id: null, name: null },
    discount: { type: "fixed", value: 0 },
    commission: { type: "fixed", value: 0 },
  });

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
  } = useForm({
    validationRules: validation,
    initialState: {
      commissionCondition: "custom",
      customConditionDiscount: "0",
      customConditionCommission: "0",
      customConditionDiscountType: "fixed",
      customConditionCommissionType: "fixed",
      settingsPerTicket: [],
    },
  });

  const {
    data: ticket,
    isLoading: ticketLoading,
    isFetching: ticketFetching,
  } = useQuery({
    queryKey: ["ticket", filters, search, eventId],
    queryFn: async () => {
      const res = await ticketList({
        ...filters,
        eventUid: eventId,
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!eventId,
  });

  const { data: details, isLoading } = useQuery({
    queryKey: ["promoter-details", data?._id],
    enabled: !!data?._id,
    queryFn: async () => {
      const res = await referVerificationDetails({ code: data?._id });
      console.log({ res });

      if (res?.status === 200) {
        setFieldsValue({
          commissionCondition: res?.data?.info?.commissionCondition,
          customConditionDiscount: res?.data?.info?.customConditionDiscount,
          customConditionCommission: res?.data?.info?.customConditionCommission,
          customConditionDiscountType:
            res?.data?.info?.customConditionDiscountType,
          customConditionCommissionType:
            res?.data?.info?.customConditionCommissionType,
          settingsPerTicket: res?.data?.info?.settingsPerTicket?.map(
            (item) => ({
              ...item,
              commission: {
                ...item?.commission,
                value:
                  item?.commission?.type === "percentage"
                    ? Number(item?.commission?.value) * 100
                    : Number(item?.commission?.value),
              },
              discount: {
                ...item?.discount,
                value:
                  item?.discount?.type === "percentage"
                    ? Number(item?.discount?.value) * 100
                    : Number(item?.discount?.value),
              },
            })
          ),
          name: res?.data?.info?.displayName,
          email: res?.data?.userData?.email,
        });
      }
      return res;
    },
  });

  const { mutate: addPromotersHandler, isPending } = useMutation({
    mutationFn: async (val) => {
      const payload = {
        ...(!data?._id
          ? {
              emails: [val?.email],
            }
          : { id: data?._id }),
        info: {
          displayName: val?.name,
          commissionCondition: val?.commissionCondition,
          customCondition: {
            discount: {
              type: val?.customConditionDiscountType,
              value:
                val?.customConditionDiscountType === "percentage"
                  ? Number(val?.customConditionDiscount) / 100
                  : Number(val?.customConditionDiscount),
            },
            commission: {
              type: val?.customConditionCommissionType,
              value:
                val?.customConditionCommissionType === "percentage"
                  ? Number(val?.customConditionCommission) / 100
                  : Number(val?.customConditionCommission),
            },
          },
          settingsPerTicket: val?.settingsPerTicket?.map((item) => ({
            ...item,
            commission: {
              ...item?.commission,
              value:
                item?.commission?.type === "percentage"
                  ? Number(item?.commission?.value) / 100
                  : Number(item?.commission?.value),
            },
            discount: {
              ...item?.discount,
              value:
                item?.discount?.type === "percentage"
                  ? Number(item?.discount?.value) / 100
                  : Number(item?.discount?.value),
            },
          })),
        },
        invitationType: "promoter-invitation",
        event: event?.data?._id,
        organization: event?.data?.organizationData?._id,
      };

      if (data?._id) {
        const res = await invitationEdit(payload);
        return res;
      } else {
        const res = await invitation(payload);
        return res;
      }
    },
    onSuccess: (res) => {
      getResponse({ res, type: "create" });
    },
  });

  const tableHeadData = [
    {
      _id: "Ticket Id",
      name: "Ticket Id",
      align: "left",
      isSort: false,
      isFilter: false,
    },
    {
      _id: "DiscountType",
      name: "Discount Type",
      align: "left",
      isSort: false,
      isFilter: false,
    },
    {
      _id: "discount",
      name: "discount",
      align: "left",
      isSort: false,
      isFilter: false,
    },
    {
      _id: "commissionType",
      name: "commission Type",
      align: "left",
      isSort: false,
      isFilter: false,
    },
    {
      _id: "commission",
      name: "commission",
      align: "left",
      isSort: false,
      isFilter: false,
    },
  ];

  const tableData = values?.settingsPerTicket?.map((ticket, i) => {
    return {
      _id: `${ticket?.ticket?.id}`,
      rowData: [
        {
          _id: "name",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] !max-w-none !w-[10%]",
          data: lngDetect(ticket?.ticket?.name) || "-",
        },
        {
          _id: "name",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] !max-w-none !w-[10%]",
          data: (
            <SelectAlt
              className={"rounded-md h-10"}
              value={ticket?.discount?.type}
              dropdownData={[
                { value: "fixed", name: "$" },
                {
                  value: "percentage",
                  name: "%",
                },
              ]}
              onChange={(val) => {
                setFieldsValue({
                  settingsPerTicket: values?.settingsPerTicket?.map((item) =>
                    item?.ticket?.id === ticket?.ticket?.id
                      ? {
                          ...item,
                          discount: { ...item?.discount, type: val },
                        }
                      : item
                  ),
                });
              }}
            />
          ),
        },
        {
          _id: "name",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] !max-w-none !w-[10%]",
          data: (
            <Input
              value={ticket?.discount?.value || 0}
              onChange={(e) => {
                const num = Number(e?.target?.value);
                if (!isNaN(num)) {
                  setFieldsValue({
                    settingsPerTicket: values?.settingsPerTicket?.map((item) =>
                      item?.ticket?.id === ticket?.ticket?.id
                        ? {
                            ...item,
                            discount: { ...item?.discount, value: num },
                          }
                        : item
                    ),
                  });
                }
              }}
            />
          ),
        },
        {
          _id: "name",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] !max-w-none !w-[10%]",
          data: (
            <SelectAlt
              className={"rounded-md h-10"}
              value={ticket?.commission?.type}
              dropdownData={[
                { value: "fixed", name: "$" },
                {
                  value: "percentage",
                  name: "%",
                },
              ]}
              onChange={(val) => {
                setFieldsValue({
                  settingsPerTicket: values?.settingsPerTicket?.map((item) =>
                    item?.ticket?.id === ticket?.ticket?.id
                      ? {
                          ...item,
                          commission: { ...item?.commission, type: val },
                        }
                      : item
                  ),
                });
              }}
            />
          ),
        },
        {
          _id: "name",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] !max-w-none !w-[10%]",
          data: (
            <Input
              value={ticket?.commission?.value || 0}
              onChange={(e) => {
                const num = Number(e?.target?.value);
                if (!isNaN(num)) {
                  setFieldsValue({
                    settingsPerTicket: values?.settingsPerTicket?.map((item) =>
                      item?.ticket?.id === ticket?.ticket?.id
                        ? {
                            ...item,
                            commission: { ...item?.commission, value: num },
                          }
                        : item
                    ),
                  });
                }
              }}
            />
          ),
        },
      ],
    };
  });

  return (
    <Form
      handleSubmit={handleSubmit}
      onSubmit={addPromotersHandler}
      className={"w-full h-full"}
    >
      <div
        className="p-5 overflow-auto max-h-[calc(100%-116px)]"
        style={{ height: "-webkit-fill-available" }}
      >
        <div>
          <h2 className="font-bold text-lg">Promoter basic info</h2>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label={"Name"}
            inputName={"name"}
            inputPlaceholder={"Enter name"}
            value={values?.name}
            onChange={handleChange}
            {...formValidate(errors, "name")}
          />
          <Input
            label={"Email"}
            inputName={"email"}
            inputType="email"
            inputPlaceholder={"Enter email"}
            value={values?.email}
            onChange={handleChange}
            {...formValidate(errors, "email")}
            isDisabled={data?._id}
          />
        </div>
        {/* ================================== */}
        <div className="mt-5">
          {/* Promoter Conditions */}
          {/* <div>
            <h2 className="font-bold text-lg">
              Promoter commission conditions
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Set up the pricing conditions for this promoter. Default discounts
              and commissions will apply to all tickets unless custom conditions
              are specified.
            </p>
            <Select
              dropdownButtonClass={"rounded-md h-10"}
              selectedValue={"custom"}
              dropdownData={[
                { name: "custom", value: "custom" },
                {
                  name: "Copy conditions from another promoter",
                  value: "copy",
                },
              ]}
            />
          </div> */}

          {/* General Custom Conditions */}
          {/* <div>
            <h2 className="font-bold text-lg">General custom conditions</h2>
            <p className="text-sm text-gray-500 mb-4">
              This setting will automatically apply to all ticket types unless a
              custom value is specified for any of them.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <div className="text-xs font-medium text-text-disabled mb-1">
                  Default discount
                </div>
                <div className="flex gap-2">
                  <Input
                    inputPlaceholder="Default Discount"
                    inputName={"customConditionDiscount"}
                    value={values?.customConditionDiscount}
                    onChange={handleChange}
                    {...formValidate(errors, "customConditionDiscount")}
                  />
                  <div className="w-20">
                    <Select
                      dropdownButtonClass={"rounded-md h-10"}
                      selectedValue={values?.customConditionDiscountType}
                      dropdownData={[
                        { value: "fixed", name: "$" },
                        {
                          value: "percentage",
                          name: "%",
                        },
                      ]}
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-xs font-medium text-text-disabled mb-1">
                  Default commission
                </div>
                <div className="flex gap-2">
                  <Input
                    inputPlaceholder="Default Commission"
                    inputName={"customConditionCommission"}
                    value={values?.customConditionCommission}
                    onChange={handleChange}
                    {...formValidate(errors, "customConditionCommission")}
                  />
                  <div className="w-20">
                    <Select
                      dropdownButtonClass={"rounded-md h-10"}
                      selectedValue={values?.customConditionCommissionType}
                      dropdownData={[
                        { value: "fixed", name: "$" },
                        {
                          value: "percentage",
                          name: "%",
                        },
                      ]}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          {/* Custom Settings per Ticket */}
          <div>
            <h2 className="font-bold text-lg">Custom settings per ticket</h2>
            <div className="mt-2">
              {values?.settingsPerTicket?.length > 0 && (
                <div>
                  <Table
                    tableHeadData={tableHeadData}
                    tableData={tableData}
                    // originalData={[...(values?.settingsPerTicket || [])]}
                    isLoder={false}
                    isFetching={false}
                  />
                </div>
              )}
              <div className="p-4 rounded-lg bg-white border border-slate-200">
                <div className="space-y-4">
                  <Select
                    placeholder={"Select ticket"}
                    label={"Tickets"}
                    dropdownButtonClass={"rounded-md h-10"}
                    selectedValue={settingsPerTicket?.ticket?.id}
                    onChange={(val, item) => {
                      setSettingsPerTicket((pre) => ({
                        ...pre,
                        ticket: { id: val, name: item?.name },
                      }));
                    }}
                    dropdownData={ticket?.data
                      ?.filter(
                        (item) =>
                          !values?.settingsPerTicket
                            ?.map((item) => item?.ticket?.id)
                            ?.includes(item?._id)
                      )
                      ?.map((item) => ({
                        value: item?._id,
                        name: lngDetect(item?.name),
                      }))}
                  />
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-text-disabled mb-1">
                        Discount
                      </div>
                      <div className="flex gap-2">
                        <Input
                          inputPlaceholder={"discount"}
                          value={settingsPerTicket?.discount?.value}
                          onChange={(e) => {
                            const num = Number(e.target.value);
                            if (!isNaN(num)) {
                              setSettingsPerTicket((pre) => ({
                                ...pre,
                                discount: {
                                  ...pre?.discount,
                                  value: String(num),
                                },
                              }));
                            }
                          }}
                        />
                        <div className="w-20">
                          <Select
                            dropdownButtonClass={"rounded-md h-10"}
                            selectedValue={settingsPerTicket?.discount?.type}
                            onChange={(val) =>
                              setSettingsPerTicket((pre) => ({
                                ...pre,
                                discount: { ...pre?.discount, type: val },
                              }))
                            }
                            dropdownData={[
                              { value: "fixed", name: "$" },
                              {
                                value: "percentage",
                                name: "%",
                              },
                            ]}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-text-disabled mb-1">
                        Commission
                      </div>
                      <div className="flex gap-2">
                        <Input
                          inputPlaceholder={"commission"}
                          value={settingsPerTicket?.commission?.value}
                          onChange={(e) => {
                            const num = Number(e.target.value);
                            if (!isNaN(num)) {
                              setSettingsPerTicket((pre) => ({
                                ...pre,
                                commission: {
                                  ...pre?.commission,
                                  value: String(num),
                                },
                              }));
                            }
                          }}
                        />
                        <div className="w-20">
                          <Select
                            dropdownButtonClass={"rounded-md h-10"}
                            selectedValue={settingsPerTicket?.commission?.type}
                            onChange={(val) =>
                              setSettingsPerTicket((pre) => ({
                                ...pre,
                                commission: { ...pre?.commission, type: val },
                              }))
                            }
                            dropdownData={[
                              { value: "fixed", name: "$" },
                              {
                                value: "percentage",
                                name: "%",
                              },
                            ]}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="mt-3 hover:bg-deep-purple/10 transition-all duration-500 flex items-center gap-2 px-3 py-2 border border-deep-purple text-deep-purple rounded-md font-medium text-xs"
                  onClick={() => {
                    if (settingsPerTicket?.ticket?.id) {
                      setFieldsValue({
                        settingsPerTicket: [
                          ...values?.settingsPerTicket,
                          settingsPerTicket,
                        ],
                      });
                      setSettingsPerTicket({
                        ticket: { id: null, name: null },
                        discount: { type: "fixed", value: 0 },
                        commission: { type: "fixed", value: 0 },
                      });
                    } else {
                      toast.info("Please select a ticket first");
                    }
                  }}
                >
                  <i className="fa-fw fa-regular fa-plus"></i> Add setting
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full sticky top-full bottom-0 left-0 p-5 bg-slate-100 flex justify-end items-center mt-auto">
        <Button
          buttonType="submit"
          buttonLabel={`${data?._id ? "Edit" : "Add"} promoter`}
          {...((isPending || isLoading) && {
            loading: true,
            buttonIconPosition: "left",
            isDisable: true,
          })}
        />
      </div>
    </Form>
  );
};

export default ManagePromoter;
